import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { IMetricSeries } from '../types/dashboard';

interface MetricGraphProps {
  title: string;
  metricType: 'cpu' | 'memory' | 'connections';
  metricSeries: IMetricSeries[];
  serviceColours: Record<string, string>;
  maxDbConnections?: number;
  height?: number;
}

const MetricGraph: React.FC<MetricGraphProps> = ({ title, metricType, metricSeries, serviceColours, maxDbConnections, height = 350 }) => {
  const allTimestampsSet = new Set<string>();
  metricSeries.forEach((series) => {
    series.data.forEach((point) => {
      allTimestampsSet.add(point.timestamp);
    });
  });
  const categories = Array.from(allTimestampsSet).sort();

  const dateStrings = categories.map((ts) => {
    const d = new Date(ts);
    return d.toISOString().slice(0, 10);
  });
  const uniqueDates = Array.from(new Set(dateStrings));
  const singleDate = uniqueDates.length === 1;

  let series: Highcharts.SeriesLineOptions[] = [];
  series = metricSeries.map((seriesObj) => {
    const pointMap = new Map<string, (typeof seriesObj.data)[0]>();
    seriesObj.data.forEach((point) => {
      pointMap.set(point.timestamp, point);
    });
    let data: (number | null)[] = [];
    if (metricType === 'cpu') {
      data = categories.map((ts) => {
        const point = pointMap.get(ts);
        return point && typeof point.cpu === 'number' ? point.cpu : null;
      });
    } else if (metricType === 'memory') {
      data = categories.map((ts) => {
        const point = pointMap.get(ts);
        return point && typeof point.memory === 'number' ? point.memory : null;
      });
    } else if (metricType === 'connections') {
      data = categories.map((ts) => {
        const point = pointMap.get(ts);
        return point && typeof point.connections === 'number' ? point.connections : null;
      });
    }
    return {
      name: `${seriesObj.service}`,
      data,
      type: 'line' as const,
      color: serviceColours[seriesObj.service] || undefined,
    };
  });

  let yAxisTitle = '';
  let yAxisMax: number | undefined = undefined;
  let yAxisPlotLines: Highcharts.YAxisPlotLinesOptions[] = [];
  if (metricType === 'cpu') {
    yAxisTitle = 'CPU Usage (%)';
    yAxisMax = 100;
  } else if (metricType === 'memory') {
    yAxisTitle = 'Memory Usage (%)';
    yAxisMax = 100;
  } else if (metricType === 'connections') {
    yAxisTitle = 'Connections';
    if (maxDbConnections) {
      // Show warning line if any value >= 70% of max
      const dbConnectionsSeries = metricSeries.find((s) => s.service === 'Database');
      const dbConnectionsData = dbConnectionsSeries
        ? categories.map((ts) => {
            const point = dbConnectionsSeries.data.find((p) => p.timestamp === ts);
            return point && typeof point.connections === 'number' ? point.connections : null;
          })
        : [];
      const showWarningLine = dbConnectionsData.some((val) => (val ?? 0) >= maxDbConnections * 0.7);
      if (showWarningLine) {
        yAxisPlotLines = [
          {
            color: 'red',
            value: maxDbConnections,
            width: 2,
            label: {
              text: `Max Connections (${maxDbConnections})`,
              align: 'right' as const,
              style: { color: 'red', fontWeight: 'bold' },
            },
          },
        ];
      }
    }
  }

  const options: Highcharts.Options = {
    title: { text: title },
    xAxis: {
      categories,
      title: { text: singleDate ? 'Time (local)' : 'Date' },
      labels: {
        step: categories.length > 50 ? 10 : categories.length > 20 ? 5 : 1,
        rotation: 0,
        style: { fontSize: '11px' },
        align: 'center',
        formatter: function () {
          const ts = this.value as string;
          const d = new Date(ts);
          if (singleDate) {
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          } else {
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${month}/${day}`;
          }
        },
      },
    },
    yAxis: {
      title: { text: yAxisTitle },
      min: 0,
      max: yAxisMax,
      plotLines: yAxisPlotLines,
    },
    series,
    credits: { enabled: false },
    chart: { height },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default MetricGraph;
